/* eslint-disable react-hooks/rules-of-hooks */
// Copyright (c) 2021 Uber Technologies, Inc.
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import classnames from 'classnames'
import { MapDrawPanelFactory } from 'kepler.gl/components'
import {
  CursorClick,
  DrawPolygon,
  EyeSeen,
  EyeUnseen,
  Polygon,
  // eslint-disable-next-line prettier/prettier, comma-dangle
  Rectangle,
} from 'kepler.gl/dist/components/common/icons'
import { MapControlButton } from 'kepler.gl/dist/components/common/styled-components'
import ToolbarItem from 'kepler.gl/dist/components/common/toolbar-item'
import { EDITOR_MODES } from 'kepler.gl/dist/constants/default-settings'
import React from 'react'
import { connect } from 'react-redux'
import styled from 'styled-components'

const MaplabToolbarItem = styled(ToolbarItem)`
  .toolbar-item__title {
    white-space: normal;
    text-align: center;
    color: ${(props) => props.theme.textColorHl};
  }
`

CustomMapDrawPanelFactory.deps = MapDrawPanelFactory.deps
function CustomMapDrawPanelFactory(MapControlTooltip, MapControlPanel, MapControlToolbar) {
  const defaultActionIcons = {
    visible: EyeSeen,
    hidden: EyeUnseen,
    polygon: DrawPolygon,
    cursor: CursorClick,
    innerPolygon: Polygon,
    rectangle: Rectangle,
    // savedPolygons: ViewListIcon,
  }
  /** @type {import('./map-draw-panel').MapDrawPanelComponent} */
  const MapDrawPanel = React.memo(
    ({
      editor,
      mapControls,
      onToggleMapControl,
      onSetEditorMode,
      publicUser,

      onToggleEditorVisibility,
      actionIcons = defaultActionIcons,
    }) => {
      // const dispatch = useDispatch()
      // const EDITOR_MODES = {
      //   ...MODES,
      //   SAVED_POLYGONS: 'SAVED_POLYGONS',
      // }
      const isActive = mapControls?.mapDraw?.active

      const onToggleMenuPanel = React.useCallback(() => onToggleMapControl('mapDraw'), [onToggleMapControl])
      // const onLoadSavedPolygons = React.useCallback(() => {
      //   dispatch(setEditorMode('SAVED_POLYGONS'))
      //   dispatch(toggleModal('USER_SAVED_POLYGONS'))
      // }, [])
      if (!mapControls?.mapDraw?.show) {
        return null
      }
      return (
        <div className='map-draw-controls' style={{ position: 'relative' }}>
          {isActive ? (
            <MapControlToolbar show={isActive}>
              {/* 
              // KEEP IT IN COMMENTS TO SHOW HOW TO ADD CUSTOM OPTION
              <MaplabToolbarItem
                className='load-saved-polygons'
                onClick={onLoadSavedPolygons}
                label='toolbar.showSavedPolygons'
                icon={actionIcons.savedPolygons}
                active={editor.mode === EDITOR_MODES.SAVED_POLYGONS}
              /> */}
              <MaplabToolbarItem
                className='edit-feature'
                onClick={() => onSetEditorMode(EDITOR_MODES.EDIT)}
                label='toolbar.select'
                icon={actionIcons.cursor}
                active={editor.mode === EDITOR_MODES.EDIT}
              />
              <MaplabToolbarItem
                className='draw-feature'
                onClick={() => onSetEditorMode(EDITOR_MODES.DRAW_POLYGON)}
                label='toolbar.polygon'
                icon={actionIcons.innerPolygon}
                active={editor.mode === EDITOR_MODES.DRAW_POLYGON}
              />
              <MaplabToolbarItem
                className='draw-rectangle'
                onClick={() => onSetEditorMode(EDITOR_MODES.DRAW_RECTANGLE)}
                label='toolbar.rectangle'
                icon={actionIcons.rectangle}
                active={editor.mode === EDITOR_MODES.DRAW_RECTANGLE}
              />
              <MaplabToolbarItem
                className='toggle-features'
                onClick={onToggleEditorVisibility}
                label={editor.visible ? 'toolbar.hide' : 'toolbar.show'}
                icon={editor.visible ? actionIcons.visible : actionIcons.hidden}
              />
            </MapControlToolbar>
          ) : null}

          <MapControlButton
            onClick={(e) => {
              e.preventDefault()
              onToggleMenuPanel()
            }}
            active={isActive}
            className={classnames('map-control-button ', 'map-draw ', { isActive })}
            data-tip
            data-for='map-draw'
          >
            <actionIcons.polygon height='22px' />
            <MapControlTooltip id='map-draw' message='tooltip.DrawOnMap' />
          </MapControlButton>
        </div>
      )
    },
  )

  MapDrawPanel.displayName = 'MapDrawPanel'
  const mapStateToProps = (state) => ({
    publicUser: state.app.user.publicUser,
  })
  const mapDispatchToProps = (dispatch) => ({})

  return connect(mapStateToProps, mapDispatchToProps)(MapDrawPanel)
}

export default CustomMapDrawPanelFactory
